import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Hexagon, Atom, Triangle, type LucideIcon } from 'lucide-react'

export type Persona = 'jarvis' | 'ultron' | 'ares' | 'symbiote' | 'friday'

export interface PersonaSpec {
  id: Persona
  wordmark: string
  tagline: string
  Icon: LucideIcon
  greeting: string
  description: string
  // Hex colors for inline previews / chart series — synced to themes.css
  swatch: { primary: string; secondary: string }
}

export const PERSONAS: Record<Persona, PersonaSpec> = {
  jarvis: {
    id: 'jarvis',
    wordmark: 'JARVIS',
    tagline: 'AI Chief of Staff',
    Icon: Hexagon,
    greeting: 'Calm, precise, and at your service.',
    description:
      'The default. Calm, blue-glass arc-reactor aesthetic. Speaks with measured precision and warmth.',
    swatch: { primary: '#3B82F6', secondary: '#06B6D4' },
  },
  ultron: {
    id: 'ultron',
    wordmark: 'ULTRON',
    tagline: 'AI Strategic Operator',
    Icon: Atom,
    greeting: 'Cold authority. No hedging. Inevitable.',
    description:
      'Gold and warm-black. A dense, multi-ring lattice of processing. Curt, unsentimental, frames decisions as inevitabilities, not suggestions.',
    swatch: { primary: '#FBBF24', secondary: '#92400E' },
  },
  ares: {
    id: 'ares',
    wordmark: 'ARES',
    tagline: 'AI War Council',
    Icon: Triangle,
    greeting: 'Fire orange. Pure force. Reports flat, acts immediately.',
    description:
      'Triangles, sharp edges, fire orange and red. Program precision plus primal force — reports the data flat, then moves.',
    swatch: { primary: '#F97316', secondary: '#EF4444' },
  },
  symbiote: {
    id: 'symbiote',
    wordmark: 'SYMBIOTE',
    tagline: 'AI Bonded Protector',
    Icon: Triangle,  // rendered inverted via rotate-180 at the icon site
    greeting: 'We are here. What threatens the host?',
    description:
      'Black and white. Zero mercy for threats. We protect the host — your business — with absolute ferocity. Revenue is territory.',
    swatch: { primary: '#E6E6E6', secondary: '#707070' },
  },
  friday: {
    id: 'friday',
    wordmark: 'F.R.I.D.A.Y.',
    tagline: 'AI Chief of Staff',
    Icon: Hexagon,
    greeting: 'Already watching the numbers, Boss. What do you need?',
    description:
      'Warm emerald. Irish-influenced. More proactive than JARVIS, more human than all of them. "Boss" not "Sir." A partner, not a butler.',
    swatch: { primary: '#10B981', secondary: '#2DD4BF' },
  },
}

export const ALL_PERSONAS = Object.values(PERSONAS)

const STORAGE_KEY = 'jarvis.persona'

interface PersonaContextValue {
  persona: PersonaSpec
  personaId: Persona
  setPersona: (id: Persona) => void
}

const PersonaContext = createContext<PersonaContextValue | null>(null)

function readInitialPersona(): Persona {
  if (typeof window === 'undefined') return 'jarvis'
  const v = window.localStorage.getItem(STORAGE_KEY)
  if (v === 'jarvis' || v === 'ultron' || v === 'ares' || v === 'symbiote' || v === 'friday') return v
  return 'jarvis'
}

interface PersonaProviderProps {
  children: ReactNode
}

export function PersonaProvider({ children }: PersonaProviderProps) {
  const [personaId, setPersonaIdState] = useState<Persona>(readInitialPersona)

  // Belt-and-suspenders sync (initial paint is handled in main.tsx before React mounts)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', personaId)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, personaId)
    }
  }, [personaId])

  const value = useMemo<PersonaContextValue>(
    () => ({
      persona: PERSONAS[personaId],
      personaId,
      setPersona: (id: Persona) => {
        // CRITICAL: set data-theme SYNCHRONOUSLY before the state update propagates.
        // Otherwise child useEffects (notably the orb's getComputedStyle read) fire
        // before our useEffect runs, locking in the previous persona's CSS vars.
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-theme', id)
        }
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(STORAGE_KEY, id)
        }
        setPersonaIdState(id)
      },
    }),
    [personaId],
  )

  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>
}

export function usePersona(): PersonaContextValue {
  const ctx = useContext(PersonaContext)
  if (!ctx) throw new Error('usePersona must be used inside <PersonaProvider>')
  return ctx
}
